import { Log } from '@/parsing/Log';
import { LogEntry, LogEntryType } from '@/parsing/LogEntry';
import { NetworkRequestOutcome } from '@/parsing/parsers/common/NetworkRequestOutcome';

import { TimelineMarker, TimelineRow, TimelineRowGroup } from '../../TimelineData';
import { obtainFreeRowForMarker } from '../obtainFreeRowForMarker';
import { TimelineGeneratorContext } from '../TimelineGeneratorContext';

export function generateNetworkGroupFromLog(
  log: Log,
  ctx: TimelineGeneratorContext,
): TimelineRowGroup {
  const processedRequests = new Set<number>();

  const rows: TimelineRow[] = [];

  for (const entry of log.entries) {
    if (
      entry.entryType !== LogEntryType.NetworkRequest &&
      entry.entryType !== LogEntryType.NetworkResponse
    ) {
      continue;
    }

    const networkRequest = entry.extra.networkRequest!;

    if (processedRequests.has(networkRequest.requestUniqueIndex)) {
      continue;
    }
    processedRequests.add(networkRequest.requestUniqueIndex);

    const start = networkRequest.request?.date;
    const end = networkRequest.response?.date;

    let textOnMarker = networkRequest.url?.pathname;
    if (textOnMarker) {
      textOnMarker = textOnMarker
        .replace('/user/user/', '/')
        .replace('/device-info/', '/')
        .replace('/search/', '/')
        .replace('/client/', '/')
        .replace('/geo/user/', '/geo/')
        .replace('/geo/location/', '/geo/')
        .replace('^/modal/', '/')
        .replace('/client-commerce-proxy/user/', '/')
        .replace('/gateway/v1/', '/')
        .replace('/campaign/campaign/v1/', '/')
        .replace('/campaign/campaign/v2/', '/')
        .replace('/rental-search/client/', '/rntl/')
        .replace('/rental-search/', '/rntl/')
        .replace('/rental-user/user/', '/rntl/')
        .replace('/rental-user/micromobility/', '/rntl/')
        .replace('/location-suggestions/', '/');
    }

    const marker: TimelineMarker = {
      markerId: ctx.nextMarkerId,
      logEntry: entry,
      alternativeLogEntry:
        typeof networkRequest.response?.logEntryIndex !== 'undefined'
          ? log.entries[networkRequest.response.logEntryIndex]
          : undefined,
      startTimeMs: 0,
      endTimeMs: undefined,
      fillColor: '#000000',
      textOnMarker,
      textOnMarkerColor: '#555',
      tooltipContent: generateTooltipContentForNetworkMarker(entry),
    };
    ctx.nextMarkerId += 1;

    if (start && end) {
      marker.startTimeMs = start.getTime();
      marker.endTimeMs = end.getTime();
    } else if (start) {
      marker.startTimeMs = start.getTime();
    } else {
      marker.startTimeMs = end!.getTime();
    }

    marker.fillColor = colorForOutcome(networkRequest.outcome);
    if (networkRequest.outcome === NetworkRequestOutcome.Failed) {
      marker.textOnMarkerColor = '#881111';
    }

    const row = obtainFreeRowForMarker(rows, marker);
    row.markers.push(marker);
  }

  const rowGroup: TimelineRowGroup = {
    name: 'Network requests',
    rows,
    expanded: true,
  };
  return rowGroup;
}

function generateTooltipContentForNetworkMarker(logEntry: LogEntry): string {
  const networkRequest = logEntry.extra.networkRequest!;
  const res = networkRequest!.response;

  let emojiStatus = '⚪️';
  switch (networkRequest!.outcome) {
    case NetworkRequestOutcome.Canceled:
      emojiStatus = '🟡';
      break;
    case NetworkRequestOutcome.Failed:
      emojiStatus = '🔴';
      break;
    case NetworkRequestOutcome.ProbablyFailed:
      emojiStatus = '🟣';
      break;
    case NetworkRequestOutcome.Succeeded:
      emojiStatus = '🟢';
      break;
  }

  const strs: string[] = [`Request #${networkRequest.requestUniqueIndex} ${emojiStatus}`];
  if (networkRequest.url?.pathname) {
    strs.push(networkRequest.url?.pathname);
  }
  if (res && res.durationSec) {
    strs.push(`${res.durationSec} sec`);
  }

  if (networkRequest.outcome === NetworkRequestOutcome.Canceled) {
    strs.push(`\n    Canceled`);
  } else if (networkRequest.outcome === NetworkRequestOutcome.ProbablyFailed) {
    strs.push(`\n    Failed (probably)`);
  }

  if (res && res.bodyJson) {
    if (typeof res.bodyJson.code !== 'undefined') {
      strs.push(`\n    "code": ${res.bodyJson.code}`);
    }
    if (typeof res.bodyJson.message !== 'undefined') {
      strs.push(`\n    "message": ${res.bodyJson.message}`);
    }
  }

  return strs.join(' ');
}

function colorForOutcome(outcome: NetworkRequestOutcome): string {
  switch (outcome) {
    case NetworkRequestOutcome.Unknown:
      return 'rgb(150, 150, 150)';
    case NetworkRequestOutcome.Canceled:
      return 'rgb(246, 227, 4)';
    case NetworkRequestOutcome.Succeeded:
      return 'rgb(0, 230, 42)';
    case NetworkRequestOutcome.Failed:
      return 'rgb(247, 91, 70)';
    case NetworkRequestOutcome.ProbablyFailed:
      return 'rgb(220, 153, 240)';
  }
}
